import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface EventRow {
  readonly eventId: string;
  readonly eventCode: string;
  readonly eventName: string;
  readonly eventCategory: string;
  readonly publisherModule: string | null;
  readonly isActive: boolean;
}

/** M29 — Event Management (§33 / §19). The publish/subscribe event catalogue:
 * each business event (Request Created, Approval Completed, Payment Released …)
 * with its publishing module and activation state. Operates the full event
 * lifecycle — register → subscribe/unsubscribe → publish → replay → delete —
 * each wired to the guarded /v1/eip/events write API. */
export function M29EventManagementPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<EventRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // create form
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // subscribe panel
  const [openSubscribeFor, setOpenSubscribeFor] = useState<string | null>(null);
  const [subscriberModule, setSubscriberModule] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: EventRow[] }>('/v1/eip/events', { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m29.event.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!name.trim()) { setActionError(t('m29.event.validationName')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: EventRow }>(`/v1/eip/events`, {
        method: 'POST',
        body: { eventName: name.trim(), eventCategory: category.trim() || undefined },
        token,
      });
      setNotice(t('m29.event.noticeRegistered', { code: r.data.eventCode }));
      setName('');
      setCategory('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, t('m29.event.verbRegister'), 'M29_EVENT_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const publish = (ev: EventRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/events/publish`, { method: 'POST', body: { eventCode: ev.eventCode, payload: {} }, token }),
      t('m29.event.noticePublished', { code: ev.eventCode }), t('m29.event.verbPublish'), 'M29_EVENT_PUBLISH',
    );

  const replay = (ev: EventRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/events/replay`, { method: 'POST', body: { eventCode: ev.eventCode, payload: {} }, token }),
      t('m29.event.noticeReplayed', { code: ev.eventCode }), t('m29.event.verbReplay'), 'M29_EVENT_PUBLISH',
    );

  const remove = (ev: EventRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/events/${ev.eventId}`, { method: 'DELETE', token }),
      t('m29.event.noticeDeleted', { code: ev.eventCode }), t('m29.event.verbDelete'), 'M29_EVENT_MANAGE',
    );

  const toggleSubscribe = (ev: EventRow): void => {
    setActionError(null);
    setOpenSubscribeFor(openSubscribeFor === ev.eventId ? null : ev.eventId);
  };

  const subscribe = async (ev: EventRow): Promise<void> => {
    if (!subscriberModule.trim()) { setActionError(t('m29.event.validationSubscriberModule')); return; }
    await runRowAction(
      () => apiRequest(`/v1/eip/events/${ev.eventId}/subscribe`, { method: 'POST', body: { subscriberModule: subscriberModule.trim() }, token }),
      t('m29.event.noticeSubscribed', { module: subscriberModule.trim(), code: ev.eventCode }), t('m29.event.verbSubscribe'), 'M29_EVENT_MANAGE',
    );
    setSubscriberModule('');
  };

  const unsubscribe = async (ev: EventRow): Promise<void> => {
    if (!subscriberModule.trim()) { setActionError(t('m29.event.validationSubscriberModule')); return; }
    await runRowAction(
      () => apiRequest(`/v1/eip/events/${ev.eventId}/unsubscribe`, { method: 'POST', body: { subscriberModule: subscriberModule.trim() }, token }),
      t('m29.event.noticeUnsubscribed', { module: subscriberModule.trim(), code: ev.eventCode }), t('m29.event.verbUnsubscribe'), 'M29_EVENT_MANAGE',
    );
    setSubscriberModule('');
  };

  const active = rows.filter((e) => e.isActive).length;

  return (
    <main aria-label="event-management">
      <h1>{t('m29.event.title')}</h1>

      <section aria-label="create-event">
        <h2>{t('m29.event.registerHeading')}</h2>
        <form aria-label="event-form" onSubmit={(e) => void create(e)}>
          <label>{t('m29.event.fieldName')}<input aria-label="field-event-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{t('m29.event.fieldCategory')}<input aria-label="field-event-category" value={category} onChange={(e) => setCategory(e.target.value)} /></label>
          <button type="submit" aria-label="submit-event" disabled={busy}>{busy ? t('m29.event.registering') : t('m29.event.register')}</button>
        </form>
        {actionError && <p role="alert" aria-label="event-error">{actionError}</p>}
        {notice && <p aria-label="event-notice">{notice}</p>}
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m29.event.loading')}</p> : (
        <>
          <p aria-label="event-count">{t('m29.event.count', { count: rows.length })}</p>
          <p aria-label="event-active">{t('m29.event.active', { count: active })}</p>
          <table aria-label="event-table">
            <thead><tr><th>{t('m29.event.colCode')}</th><th>{t('m29.event.colName')}</th><th>{t('m29.event.colCategory')}</th><th>{t('m29.event.colPublisher')}</th><th>{t('m29.event.colState')}</th><th>{t('m29.event.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((ev) => (
                <tr key={ev.eventId}>
                  <td>{ev.eventCode}</td><td>{ev.eventName}</td><td>{ev.eventCategory}</td>
                  <td>{ev.publisherModule ?? '—'}</td><td>{ev.isActive ? t('m29.event.stateActive') : t('m29.event.stateInactive')}</td>
                  <td>
                    <button type="button" aria-label={`publish-${ev.eventCode}`} onClick={() => void publish(ev)}>{t('m29.event.btnPublish')}</button>
                    <button type="button" aria-label={`replay-${ev.eventCode}`} onClick={() => void replay(ev)}>{t('m29.event.btnReplay')}</button>
                    <button type="button" aria-label={`subscribe-panel-${ev.eventCode}`} onClick={() => toggleSubscribe(ev)}>{t('m29.event.btnSubscribers')}</button>
                    <button type="button" aria-label={`delete-${ev.eventCode}`} onClick={() => void remove(ev)}>{t('m29.event.btnDelete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {openSubscribeFor && (() => {
            const ev = rows.find((r) => r.eventId === openSubscribeFor);
            if (!ev) return null;
            return (
              <section aria-label="subscribe-panel">
                <h2>{t('m29.event.subscribersHeading', { code: ev.eventCode })}</h2>
                <label>{t('m29.event.fieldSubscriberModule')}<input aria-label="field-subscriber-module" value={subscriberModule} onChange={(e) => setSubscriberModule(e.target.value)} /></label>
                <button type="button" aria-label="subscribe" onClick={() => void subscribe(ev)}>{t('m29.event.btnSubscribe')}</button>
                <button type="button" aria-label="unsubscribe" onClick={() => void unsubscribe(ev)}>{t('m29.event.btnUnsubscribe')}</button>
              </section>
            );
          })()}
        </>
      )}
    </main>
  );
}
